/*******************************************************************************
 * Copyright (c) 2023 GetChkd
 *
 * All rights reserved.
 *
 * Contributors:
 *  James Worthington - Initial implementation
 *  Amina Rais - Massive Improvements
 *  Version: 2.0.0
 *
 *******************************************************************************/


import { Grid, CircularProgress } from '@mui/material';
import { useCallback, useEffect, useState } from 'react'
import { Box } from '@mui/system';
import { getAllOrganizationApplications } from '../../../../../store/reducers/organization/applications/orgApplicationAction';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../../../../components/ui-elements/loader';
import { MdSettingsApplications } from "react-icons/md";

import ContentCard from '../../../../../components/ui-elements/cards/ContentCard';
import BackButton from '../../../../../components/shared/Buttons/BackButton';
import { useParams } from 'react-router-dom';
import { getAllByOrganizationAppicationID, getLoginIdp } from '../../../../../services/organization/OrgAppUserController';

const OrganizationApplicationPage = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const orgApplications = useSelector(state => state.orgApplications.orgApplications);
    const { orgid } = useParams();
    const [assertionUrls, setAssertionUrls] = useState({});
    const user = useSelector(state => state.user.user);
    const [loadingAppId, setLoadingAppId] = useState(null);

    // Function to fetch user info from the Redux store
    const fetchOrgApplicationInfo = useCallback(async () => {
        try {

            await dispatch(getAllOrganizationApplications({ "organizationID": orgid })).unwrap();
            setLoading(false);

        } catch (error) {
            console.error("Error fetching info:", error);
        } finally {
            setLoading(false);
        }
    }, [dispatch, orgid]);

    // Fetch user info on component mount
    useEffect(() => {
        fetchOrgApplicationInfo();
    }, [fetchOrgApplicationInfo]);
    // Fetch user info on component mount
    useEffect(() => {
        if (!orgApplications?.Records?.length) return;

        const appNames = ["personnel", "splunk", "asset location", "salesforce"];
        const urls = {};

        (async () => {
            for (const name of appNames) {
                const matchedApp = orgApplications.Records.find(
                    (app) => app.applicationName?.toLowerCase() === name
                );

                if (matchedApp) {
                    try {
                        const orgAppDetails = await getAllByOrganizationAppicationID({
                            organizationApplicationID: matchedApp.organizationApplicationID,
                        });

                        const assertionUrl = orgAppDetails.Records?.[0]?.assertionConsumerServiceURL;
                        urls[name] = assertionUrl;
                        console.log(`Found ${name} URL:`, assertionUrl);
                    } catch (error) {
                        console.error(`Failed to fetch details for ${name}:`, error);
                    }
                } else {
                    console.warn(`${name} not found`);
                }
            }
            setAssertionUrls(urls);

            console.log("All URLs:", urls);
        })();
    }, [orgApplications]);


    const handleSalesForceCase = async (url, organizationApplicationID) => {
        if (!url) {
            console.error("Callback URL is not ready.");
            return;
        }


        try {
            const data = {
                username: user.username,
                type: "SAML",
                password: "",
                organizationApplicationID: organizationApplicationID,
                organizationID: orgid
            };

            console.log('url', url)
            const response = await getLoginIdp(data);
            console.log('response', response)
            if (!response.token) {
                alert("Login failed. Please try again.");
                return;
            }

            // Dynamically create and submit a form to callbackUrl
            const form = document.createElement("form");
            form.method = "POST";
            form.action = url;
            form.target = "_blank"; // <- open in new tab

            const input = document.createElement("input");
            input.type = "hidden";
            input.name = "SAMLResponse";
            input.value = response.token;

            form.appendChild(input);
            document.body.appendChild(form);
            form.submit();

            // Cleanup
            document.body.removeChild(form);
            setLoadingAppId(null);

        } catch (error) {
            setLoadingAppId(null);

            console.error("SAML login failed:", error);
        }
    };


    const handleApplications = (application) => {
        setLoadingAppId(application.organizationApplicationID);

        const appName = application.applicationName?.toLowerCase();
        const url = assertionUrls[appName];

        const token = localStorage.getItem('getchkdjwt');
        const parsedData = JSON.parse(token);
        const parsedtoken = parsedData.token;
        switch (appName) {
            case "salesforce":
                handleSalesForceCase(url, application.organizationApplicationID);

                break;
            case "personnel":
                if (url && token) {
                    // Ensure there's no trailing slash.
                    const normalizedUrl = url.endsWith('/') ? url.slice(0, -1) : url;

                    const popup = window.open(normalizedUrl, '_blank');
                    if (popup) {
                        popup.name = JSON.stringify({
                            token: parsedtoken ?? "",
                            orgappid: application.organizationApplicationID ?? "",
                            orgid: application.organizationID ?? "",
                        });
                    }
                    setLoadingAppId(null);

                } else {
                    alert(`No assertion URL or token found for ${appName}`);
                    setLoadingAppId(null);

                }
                break;
            case "asset location":
                if (url && token) {
                    const normalizedUrl = url.endsWith('/') ? url.slice(0, -1) : url;

                    const popup = window.open(normalizedUrl, '_blank');
                    if (popup) {
                        popup.name = JSON.stringify({
                            token: parsedtoken ?? "",
                            orgappid: application.organizationApplicationID ?? "",
                            orgid: application.organizationID ?? "",
                        });
                    }
                    setLoadingAppId(null);

                } else {
                    setLoadingAppId(null);

                    alert(`No assertion URL or token found for ${appName}`);
                }
                break;
            case "splunk":
                if (url) {
                    window.open(url, '_blank', 'noopener,noreferrer');
                    setLoadingAppId(null);

                } else {
                    setLoadingAppId(null);

                    alert(`No assertion URL found for ${appName}`);
                }

                break;
            default:
                if (url) {
                    window.open(url, '_blank', 'noopener,noreferrer');
                    setLoadingAppId(null);

                } else {
                    setLoadingAppId(null);

                    alert(`No assertion URL found for ${appName}`);
                }
                break;
        }


    }
    return (
        <>
            <Box m={6}>
                {/* Back button with title */}
                <BackButton pageTitle="Organization Application" />
                <Box m={6}>

                    {loading ? (
                        <Loader />
                    ) : (
                        <><Grid container spacing={4}  >

                            {orgApplications?.Records?.length > 0 && orgApplications?.Records?.map((application) => {

                                return (

                                    <Grid item xs={12} sm={6} md={3} key={application.organizationApplicationID}>

                                        <ContentCard
                                            onClick={() => handleApplications(application)}

                                            title={application.applicationName}
                                            subTitle={application.organizationName}
                                            icon={
                                                loadingAppId === application.organizationApplicationID ? (
                                                    <CircularProgress size={24} color="inherit" />
                                                ) : (
                                                    <MdSettingsApplications />
                                                )
                                            }>
                                        </ContentCard>

                                    </Grid>
                                );

                            })}


                        </Grid>
                        </>)}
                </Box>

            </Box >

        </>
    )

}

export default OrganizationApplicationPage;
